<template>
  <v-container id="location-details" fluid>
    <v-card outlined>
      <validation-observer ref="form">
        <v-form @submit.prevent="onSubmit">
          <v-tabs v-model="selectedTab" grow>
            <v-tab>{{ $t('location.basicInformation') }}</v-tab>
            <v-tab>{{ $t('location.schedules') }}</v-tab>
            <v-tab>{{ $t('location.settings') }}</v-tab>
            <v-tab v-if="isAdmin">{{ $t('location.custom') }}</v-tab>
            <v-tab-item>
              <v-container fluid>
                <v-row>
                  <v-col cols="12" sm="5">
                    <v-card outlined>
                      <v-card-title>
                        <div class="overline mb-4">{{ $t('location.references') }}</div>
                      </v-card-title>
                      <v-container>
                        <validation-provider
                          v-slot="{ errors }"
                          :name="$t('location.locationIdentifier')"
                          rules="required|identifier"
                        >
                          <v-text-field
                            v-model="location.location_id"
                            :disabled="location._id != undefined"
                            :error-messages="errors"
                            :label="$t('location.locationIdentifier')"
                            required
                          ></v-text-field>
                        </validation-provider>
                        <validation-provider v-slot="{ errors }" :name="$t('location.locationType')" rules="required">
                          <v-select
                            v-model="location.type"
                            :error-messages="errors"
                            :items="typeOptions"
                            :label="$t('location.locationType')"
                            required
                          ></v-select>
                        </validation-provider>
                        <v-select
                          v-if="location.type === 'shop'"
                          v-model="location.establishment_type"
                          :items="establishmentTypeOptions"
                          :label="$t('location.establishmentType')"
                          required
                        ></v-select>
                      </v-container>
                    </v-card>
                    <v-card class="mt-5" outlined>
                      <v-list-item>
                        <v-list-item-content>
                          <div class="overline mb-4">{{ $t('location.contact') }}</div>
                        </v-list-item-content>
                      </v-list-item>
                      <v-container>
                        <validation-provider v-slot="{ errors }" :name="$t('location.name')" rules="required">
                          <v-text-field
                            v-model="location.name"
                            :error-messages="errors"
                            :label="$t('location.name')"
                            required
                          ></v-text-field>
                        </validation-provider>
                        <validation-provider v-slot="{ errors }" :name="$t('location.email')" rules="email">
                          <v-text-field
                            v-model="location.contact.email"
                            :error-messages="errors"
                            :label="$t('location.email')"
                          ></v-text-field>
                        </validation-provider>
                        <v-text-field
                          v-model="location.contact.phone_number"
                          :label="$t('location.phone')"
                        ></v-text-field>
                      </v-container>
                    </v-card>
                  </v-col>
                  <v-col cols="12" sm="7">
                    <v-card outlined>
                      <v-card-title>
                        <div class="overline mb-4">{{ $t('location.address') }}</div>
                      </v-card-title>
                      <v-container>
                        <validation-provider v-slot="{ errors }" :name="$t('location.address') + ' 1'" rules="required">
                          <v-text-field
                            v-model="location.address.address_line_1"
                            :error-messages="errors"
                            :label="$t('location.address') + ' 1'"
                            required
                          ></v-text-field>
                        </validation-provider>
                        <v-text-field
                          v-model="location.address.address_line_2"
                          :label="$t('location.address') + ' 2'"
                        ></v-text-field>
                        <v-row>
                          <v-col md="4">
                            <validation-provider v-slot="{ errors }" :name="$t('location.postalCode')" rules="required">
                              <v-text-field
                                v-model="location.address.postal_code"
                                :error-messages="errors"
                                :label="$t('location.postalCode')"
                                required
                              ></v-text-field>
                            </validation-provider>
                          </v-col>
                          <v-col md="4">
                            <validation-provider v-slot="{ errors }" :name="$t('location.city')" rules="required">
                              <v-text-field
                                v-model="location.address.city"
                                :error-messages="errors"
                                :label="$t('location.city')"
                                required
                              ></v-text-field>
                            </validation-provider>
                          </v-col>
                          <v-col md="4">
                            <validation-provider v-slot="{ errors }" :name="$t('location.country')" rules="required">
                              <countries-autocomplete
                                v-model="location.address.country_id"
                                :errorMessage="errors"
                                :label="$t('location.country')"
                                :required="true"
                              />
                            </validation-provider>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col md="4">
                            <v-text-field
                              v-model="location.address.world_area"
                              :label="$t('location.worldArea')"
                              class="zone-uppercase"
                            ></v-text-field>
                          </v-col>
                          <v-col md="4">
                            <v-combobox
                              v-model="location.address.country_area_1"
                              :items="area1Options"
                              :filter="areaFilter"
                              :label="$t('location.countryArea1')"
                              :placeholder="$t('location.selectOrTypeArea')"
                              :disabled="!location.address.country_id"
                              :menu-props="{ contentClass: 'zone-uppercase-menu' }"
                              class="zone-uppercase"
                              clearable
                              @change="onAreaCommit(1)"
                              @update:search-input="area1Search = $event"
                            >
                              <!-- v-combobox ignores the no-data-text prop: the slot is required
                                   for the menu to open when empty and show the message. -->
                              <template #no-data>
                                <v-list-item>
                                  <v-list-item-content style="white-space: normal">{{ $t('location.noAreaYet') }}</v-list-item-content>
                                </v-list-item>
                              </template>
                              <!-- Flag a value absent from the reference set as a new area. -->
                              <template #item="{ item }">
                                <span class="text-uppercase">{{ item }}</span>
                                <v-chip
                                  v-if="!existingArea1Names.includes(item)"
                                  small
                                  outlined
                                  color="primary"
                                  class="ml-2 px-3 text-none"
                                >
                                  {{ $t('location.newArea') }}
                                </v-chip>
                              </template>
                            </v-combobox>
                          </v-col>
                          <v-col md="4">
                            <v-combobox
                              v-model="location.address.country_area_2"
                              :items="area2Options"
                              :filter="areaFilter"
                              :label="$t('location.countryArea2')"
                              :placeholder="$t('location.selectOrTypeArea')"
                              :disabled="!location.address.country_area_1"
                              :menu-props="{ contentClass: 'zone-uppercase-menu' }"
                              class="zone-uppercase"
                              clearable
                              @change="onAreaCommit(2)"
                              @update:search-input="area2Search = $event"
                            >
                              <!-- v-combobox ignores the no-data-text prop: the slot is required
                                   for the menu to open when empty and show the message. -->
                              <template #no-data>
                                <v-list-item>
                                  <v-list-item-content style="white-space: normal">{{ $t('location.noAreaYet') }}</v-list-item-content>
                                </v-list-item>
                              </template>
                              <!-- Flag a value absent from the reference set as a new area. -->
                              <template #item="{ item }">
                                <span class="text-uppercase">{{ item }}</span>
                                <v-chip
                                  v-if="!existingArea2Names.includes(item)"
                                  small
                                  outlined
                                  color="primary"
                                  class="ml-2 px-3 text-none"
                                >
                                  {{ $t('location.newArea') }}
                                </v-chip>
                              </template>
                            </v-combobox>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col md="6">
                            <validation-provider v-slot="{ errors }" name="latitude" rules="min_value:-90|max_value:90">
                              <v-text-field
                                v-model="computedLatitude"
                                :error-messages="errors"
                                :label="$t('location.latitude')"
                              ></v-text-field>
                            </validation-provider>
                          </v-col>
                          <v-col md="6">
                            <validation-provider
                              v-slot="{ errors }"
                              name="longitude"
                              rules="min_value:-180|max_value:180"
                            >
                              <v-text-field
                                v-model="computedLongitude"
                                :error-messages="errors"
                                :label="$t('location.longitude')"
                              ></v-text-field>
                            </validation-provider>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col>
                            <validation-provider v-slot="{ errors }" :name="$t('location.timezone')" rules="required">
                              <v-combobox
                                v-model="location.timezone"
                                :error-messages="errors"
                                :items="timezones"
                                :label="$t('location.timezone')"
                                :multiple="false"
                              ></v-combobox>
                            </validation-provider>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
            <v-tab-item>
              <v-container class="pt-2 pb-1" fluid>
                <v-card outlined>
                  <v-card-title>
                    <div class="overline mb-4">{{ $t('location.openingHours') }}</div>
                  </v-card-title>
                  <v-card-text>
                    <v-row v-for="(timeSlots, day) in location?.opening_hours" v-bind:key="day">
                      <v-col class="pb-1 pt-1" cols="12">
                        <v-divider />
                      </v-col>
                      <v-col class="d-flex flex-column flex-lg-row align-center">
                        <v-col class="pt-0 pb-0" cols="2">
                          <v-list-item class="pl-2">
                            <span class="pr-0 pl-1">{{ $t('location.' + day) }}</span>
                          </v-list-item>
                        </v-col>
                        <v-col cols="8">
                          <v-row>
                            <v-col v-for="(timeSlot, index) in timeSlots" v-bind:key="index" class="pb-0 pt-2">
                              <v-row class="pl-3" justify="start">
                                <validation-provider v-slot="{ errors }" :name="$t('location.from')" rules="required">
                                  <v-text-field
                                    v-model="timeSlot.begin"
                                    :error-messages="errors"
                                    :label="$t('location.from')"
                                    dense
                                    type="time"
                                  ></v-text-field>
                                </validation-provider>
                                <validation-provider
                                  v-slot="{ errors }"
                                  :name="$t('location.to')"
                                  :rules="'required|end_after_begin_datetime:' + timeSlot.begin + ',' + timeSlot.end"
                                >
                                  <v-text-field
                                    v-model="timeSlot.end"
                                    :error-messages="errors"
                                    :label="$t('location.to')"
                                    dense
                                    type="time"
                                  ></v-text-field>
                                </validation-provider>
                                <v-icon class="mr-2" @click="removeTimeSlot(index, timeSlots)">mdi-close</v-icon>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-col>
                      <v-col class="d-flex justify-end align-center" cols="2">
                        <v-btn color="primary" outlined @click="addTimeSlot(timeSlots)">
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="timeSlots.length && validateTimeSlots(timeSlots)"
                          color="primary"
                          class="ml-2"
                          outlined
                          @click="selectTimeSlotsToDuplicate('location.openingHours', 'opening_hours', day)"
                        >
                          <v-icon>$vuetify.icons.copy</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
                <v-card
                  class="mt-1"
                  outlined
                  v-for="(service, index) of location.service_opening_hours"
                  v-bind:key="index"
                >
                  <v-card-title class="align-center justify-space-between">
                    <div class="overline d-flex pt-0">
                      <v-switch
                        class="mt-0 mb-0"
                        dense
                        hide-details
                        v-model="service.activate"
                        @change="$set(service, 'showModal', true)"
                      />
                      <span>
                        {{ $t('location.serviceHours.hoursOf') + ' ' + $t('location.serviceHours.' + service.name) }}
                      </span>
                    </div>
                    <v-btn
                      color="primary"
                      :class="isMobile ? 'btn-mobile' : 'btn'"
                      :disabled="!service.activate"
                      outlined
                      @click="$set(service, 'showCopyOpeningHoursModal', true)"
                    >
                      {{
                        isMobile ? $t('location.serviceHours.copyHours') : $t('location.serviceHours.copyOpeningHours')
                      }}
                    </v-btn>
                  </v-card-title>
                  <v-card-text v-show="service.activate">
                    <v-row v-for="(timeSlots, day) of service.opening_hours" v-bind:key="day">
                      <v-col class="pb-1 pt-1" cols="12">
                        <v-divider />
                      </v-col>
                      <v-col class="d-flex flex-column flex-lg-row align-center">
                        <v-col class="pt-0 pb-0" cols="2">
                          <v-list-item class="pl-2">
                            <span class="pr-0 pl-1">{{ $t('location.' + day) }}</span>
                          </v-list-item>
                        </v-col>
                        <v-col cols="8">
                          <v-row>
                            <!-- change index for something else as key -->
                            <v-col v-for="(timeSlot, index) in timeSlots" v-bind:key="index" class="pb-0 pt-2">
                              <v-row class="pl-3" justify="start">
                                <validation-provider v-slot="{ errors }" :name="$t('location.from')" rules="required">
                                  <v-text-field
                                    v-model="timeSlot.begin"
                                    :error-messages="errors"
                                    :label="$t('location.from')"
                                    dense
                                    type="time"
                                  ></v-text-field>
                                </validation-provider>
                                <validation-provider
                                  v-slot="{ errors }"
                                  :name="$t('location.to')"
                                  :rules="'required|end_after_begin_datetime:' + timeSlot.begin + ',' + timeSlot.end"
                                >
                                  <v-text-field
                                    v-model="timeSlot.end"
                                    :error-messages="errors"
                                    :label="$t('location.to')"
                                    dense
                                    type="time"
                                  ></v-text-field>
                                </validation-provider>
                                <v-icon class="mr-2" @click="removeTimeSlot(index, timeSlots)">mdi-close</v-icon>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-col>
                      <v-col class="d-flex justify-end align-center" cols="2">
                        <v-btn color="primary" outlined @click="addTimeSlot(timeSlots)">
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="timeSlots.length && validateTimeSlots(timeSlots)"
                          color="primary"
                          class="ml-2"
                          outlined
                          @click="
                            selectTimeSlotsToDuplicate(
                              'location.serviceHours.' + service.name,
                              'service_opening_hours',
                              day,
                              index,
                              true
                            )
                          "
                        >
                          <v-icon>$vuetify.icons.copy</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>

                  <confirmation-dialog
                    persistent
                    :key="`modal-${service.name}`"
                    :title="$t('location.serviceHours.confirm')"
                    :text="
                      $t(
                        service.activate
                          ? 'location.serviceHours.activateServiceModalTitle'
                          : 'location.serviceHours.disabledServiceModalTitle',
                        { service: $t('location.serviceHours.' + service.name) }
                      )
                    "
                    :value="service.showModal"
                    @yes="toggleHoursServices(service, true)"
                    @no="toggleHoursServices(service, false)"
                    :reverse-primary="true"
                  />

                  <confirmation-dialog
                    persistent
                    :key="`modal-${service.name}-copy`"
                    :title="$t('location.serviceHours.copyOpeningHoursModalTitle')"
                    :text="
                      $t('location.serviceHours.copyOpeningHoursModalText', {
                        service: $t('location.serviceHours.' + service.name),
                      })
                    "
                    :value="service.showCopyOpeningHoursModal"
                    @yes="
                      copyOpeningHours(service)
                      $set(service, 'showCopyOpeningHoursModal', false)
                    "
                    @no="$set(service, 'showCopyOpeningHoursModal', false)"
                    :reverse-primary="true"
                  />
                </v-card>

                <v-card class="mt-1" outlined>
                  <v-card-title>
                    <div class="overline mb-4">{{ $t('location.exceptionalHours') }}</div>
                  </v-card-title>
                  <v-card-text>
                    <v-row
                      v-for="(timeSlots, indexDate) in location.exceptional_opening_hours"
                      v-bind:key="timeSlots.date"
                    >
                      <v-col class="pb-1 pt-1" cols="12">
                        <v-divider />
                      </v-col>
                      <v-col
                        class="d-flex flex-column flex-lg-row"
                        cols="8"
                        :sm="exceptionalHourInModificationIndex !== indexDate ? 10 : 8"
                      >
                        <v-row>
                          <v-col class="pt-0 pb-0 d-flex justify-start flex-column" cols="12" sm="2">
                            <v-list-item class="pl-2">
                              <span class="pr-0 pl-1">{{ timeSlots.date | moment('L') }}</span>
                            </v-list-item>
                          </v-col>
                          <v-col cols="12" sm="10">
                            <v-row v-if="timeSlots.hours.length">
                              <!-- change index for something else as key -->
                              <v-col
                                v-for="(timeSlot, index) in timeSlots.hours"
                                v-bind:key="index"
                                class="pt-2"
                                xl="3"
                                :lg="exceptionalHourInModificationIndex !== indexDate ? 4 : 6"
                                :sm="exceptionalHourInModificationIndex !== indexDate ? 6 : 12"
                                cols="12"
                              >
                                <v-row class="pl-3" style="width: max-content" justify="start">
                                  <validation-observer
                                    :ref="'exceptionalHoursModifications' + indexDate"
                                    class="d-flex flex-wrap"
                                  >
                                    <validation-provider
                                      v-slot="{ errors }"
                                      :name="$t('location.from')"
                                      rules="required"
                                    >
                                      <v-text-field
                                        class="pt-4 mr-2"
                                        v-model="timeSlot.begin"
                                        :disabled="!(exceptionalHourInModificationIndex === indexDate)"
                                        :error-messages="errors"
                                        :label="$t('location.from')"
                                        dense
                                        type="time"
                                      ></v-text-field>
                                    </validation-provider>
                                    <validation-provider
                                      v-slot="{ errors }"
                                      :name="$t('location.to')"
                                      :rules="
                                        'required|end_after_begin_datetime:' + timeSlot.begin + ',' + timeSlot.end
                                      "
                                    >
                                      <v-text-field
                                        class="pt-4"
                                        v-model="timeSlot.end"
                                        :disabled="!(exceptionalHourInModificationIndex == indexDate)"
                                        :error-messages="errors"
                                        :label="$t('location.to')"
                                        dense
                                        type="time"
                                      ></v-text-field>
                                    </validation-provider>
                                  </validation-observer>
                                  <v-icon
                                    v-if="exceptionalHourInModificationIndex == indexDate"
                                    class="mr-2"
                                    @click="removeTimeSlot(index, timeSlots.hours)"
                                  >
                                    mdi-close
                                  </v-icon>
                                </v-row>
                              </v-col>
                            </v-row>
                            <v-row v-else>
                              <v-col>
                                <v-card-text
                                  class="text-left pl-2 pl-sm-4 pt-0 pt-sm-4 text-sm-center text-caption text-md-body-2"
                                >
                                  {{ $t('location.locationIsClosed') }}
                                </v-card-text>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col
                        v-if="exceptionalHourInModificationIndex !== indexDate"
                        class="d-flex justify-end align-center"
                        cols="4"
                        sm="2"
                      >
                        <v-btn color="primary" outlined @click="activateExceptionalHourEdition(indexDate)">
                          <v-icon>mdi-calendar-edit-outline</v-icon>
                        </v-btn>
                      </v-col>
                      <v-col
                        v-else
                        :class="
                          $vuetify.breakpoint.width < 550
                            ? 'd-flex justify-center align-end flex-column'
                            : 'd-flex justify-end align-center'
                        "
                        cols="4"
                      >
                        <v-btn
                          color="primary"
                          :class="$vuetify.breakpoint.width < 550 ? 'mb-1' : 'mr-1'"
                          outlined
                          @click="addTimeSlot(timeSlots.hours)"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn
                          color="primary"
                          :class="$vuetify.breakpoint.width < 550 ? 'mb-1' : 'mr-1'"
                          outlined
                          @click="validateExceptionalHourEdition(indexDate, timeSlots.hours)"
                        >
                          <v-icon>mdi-check</v-icon>
                        </v-btn>
                        <v-btn
                          color="primary"
                          :class="$vuetify.breakpoint.width < 550 ? '' : 'mr-1'"
                          outlined
                          @click="removeExceptionalTimeSlot(indexDate, location.exceptional_opening_hours)"
                        >
                          <v-icon>mdi-delete-forever</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="pb-1 pt-1" cols="12">
                        <v-divider />
                      </v-col>
                    </v-row>
                    <v-row v-if="addingNewExceptionalHour">
                      <v-col class="d-flex flex-column flex-lg-row" cols="8">
                        <validation-observer ref="exceptionalHour" class="row" tag="div">
                          <v-col
                            class="pt-0 pb-0 d-flex justify-center align-sm-center align-start flex-column"
                            cols="12"
                            sm="3"
                          >
                            <v-menu
                              ref="menu"
                              v-model="menu"
                              :close-on-content-click="false"
                              min-width="290px"
                              offset-y
                              transition="scale-transition"
                            >
                              <template v-slot:activator="{ on }">
                                <validation-provider
                                  v-slot="{ errors }"
                                  :rules="
                                    'required|date_do_not_exist:' +
                                    JSON.stringify(existingExceptionalHoursDates) +
                                    '|not_in_the_past:' +
                                    newExceptionalHours.date
                                  "
                                  :name="$t('location.date')"
                                >
                                  <v-text-field
                                    v-model="newExceptionalHoursDisplayedDate"
                                    :error-messages="errors"
                                    :placeholder="$t('location.pickDate')"
                                    dense
                                    hide-details="auto"
                                    outlined
                                    readonly
                                    v-on="on"
                                  ></v-text-field>
                                </validation-provider>
                              </template>
                              <v-date-picker
                                v-if="menu"
                                v-model="newExceptionalHours.date"
                                :min="minDateExceptionalHours"
                                :locale="locale"
                                :first-day-of-week="firstDayOfWeek"
                                no-title
                              >
                                <v-spacer></v-spacer>
                                <v-btn color="primary" text @click="menu = false">
                                  {{ $t('components.modal.btn.validate') }}
                                </v-btn>
                              </v-date-picker>
                            </v-menu>
                          </v-col>
                          <v-col cols="12" sm="9">
                            <div
                              v-if="
                                newExceptionalHours &&
                                Array.isArray(newExceptionalHours.hours) &&
                                newExceptionalHours.hours.length
                              "
                            >
                              <v-row v-for="(timeSlot, index) in newExceptionalHours.hours" v-bind:key="index">
                                <v-col cols="5">
                                  <validation-provider
                                    v-slot="{ errors }"
                                    :name="$t('location.newExceptionalHours.begin', { index: index })"
                                    rules="required"
                                  >
                                    <v-text-field
                                      v-model="timeSlot.begin"
                                      :error-messages="errors"
                                      :label="$t('location.from')"
                                      class="mr-2"
                                      dense
                                      type="time"
                                    ></v-text-field>
                                  </validation-provider>
                                </v-col>
                                <v-col cols="5">
                                  <validation-provider
                                    v-slot="{ errors }"
                                    :name="$t('location.newExceptionalHours.end', { index: index })"
                                    :rules="
                                      'required|end_after_begin_datetime:' +
                                      newExceptionalHours.hours[0].begin +
                                      ',' +
                                      newExceptionalHours.hours[0].end
                                    "
                                  >
                                    <v-text-field
                                      v-model="timeSlot.end"
                                      :error-messages="errors"
                                      :label="$t('location.to')"
                                      class="mr-2"
                                      dense
                                      type="time"
                                    ></v-text-field>
                                  </validation-provider>
                                </v-col>
                                <v-col cols="2" class="d-flex justify-start align-center">
                                  <v-icon @click="removeTimeSlot(index, newExceptionalHours.hours)">mdi-close</v-icon>
                                </v-col>
                              </v-row>
                            </div>
                            <v-row v-else>
                              <v-col>
                                <v-card-text
                                  class="text-left pl-2 pl-sm-4 pt-0 pt-sm-4 text-sm-center text-caption text-md-body-2"
                                >
                                  {{ $t('location.locationIsClosed') }}
                                </v-card-text>
                              </v-col>
                            </v-row>
                          </v-col>
                        </validation-observer>
                      </v-col>
                      <v-col
                        :class="
                          $vuetify.breakpoint.width < 550
                            ? 'd-flex justify-center align-end flex-column'
                            : 'd-flex justify-end align-center'
                        "
                        cols="4"
                      >
                        <v-btn
                          :class="$vuetify.breakpoint.width < 550 ? 'mb-1' : 'mr-1'"
                          color="primary"
                          outlined
                          @click="addTimeSlot(newExceptionalHours.hours)"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn
                          :class="$vuetify.breakpoint.width < 550 ? 'mb-1' : 'mr-1'"
                          color="primary"
                          outlined
                          @click="validateExceptionalHoursDate"
                        >
                          <v-icon>mdi-check</v-icon>
                        </v-btn>
                        <v-btn
                          :class="$vuetify.breakpoint.width < 550 ? '' : 'mr-1'"
                          color="primary"
                          outlined
                          @click="cancelAddNewExceptionalHour"
                        >
                          <v-icon>mdi-delete-forever</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row v-else justify="center">
                      <v-btn class="ma-2" color="primary" outlined @click="addNewExceptionalHour">
                        {{ $t('location.addNewDate') }}
                      </v-btn>
                    </v-row>
                  </v-card-text>
                </v-card>
                <v-card class="mt-1" outlined>
                  <v-card-title>
                    <div class="overline mb-4">{{ $t('location.specialClosures') }}</div>
                  </v-card-title>
                  <v-container class="pt-0 pb-1" fluid>
                    <v-row v-for="(specialClosure, index) in location.special_closures" v-bind:key="specialClosure._id">
                      <v-col class="pb-1 pt-1" cols="12">
                        <v-divider />
                      </v-col>
                      <v-col cols="12">
                        <v-row justify="center">
                          <v-list-item class="pl-2">
                            <v-spacer />
                            <span v-if="specialClosureInModificationIndex == index" class="d-inline-flex">
                              <v-btn
                                :small="$vuetify.breakpoint.width < 400"
                                class="mr-1"
                                color="primary"
                                outlined
                                @click="validateSpecialClosureEdition(index)"
                              >
                                <v-icon>mdi-check</v-icon>
                              </v-btn>
                              <v-btn
                                :small="$vuetify.breakpoint.width < 400"
                                color="primary"
                                outlined
                                @click="specialClosureDeletion = true"
                              >
                                <v-icon>mdi-delete-forever</v-icon>
                              </v-btn>
                            </span>
                            <v-btn v-else color="primary" outlined @click="activateSpecialClosureEdition(index)">
                              <v-icon>mdi-calendar-edit-outline</v-icon>
                            </v-btn>
                          </v-list-item>
                        </v-row>
                      </v-col>
                      <o-date-time-range-filter
                        v-model="location.special_closures[index]"
                        :disabled="!(specialClosureInModificationIndex == index)"
                        :validation-ref="'specialClosuresModifications'"
                        :ref="'specialClosuresModifications'"
                      />
                      <confirmation-dialog
                        v-if="specialClosureDeletion"
                        v-model="specialClosureDeletion"
                        :text="$t('location.deletionSpecialClosureText')"
                        :title="$t('location.deletionSpecialClosureTitle')"
                        @no="specialClosureDeletion = false"
                        @yes="removeSpecialClosure(index, location.special_closures)"
                      ></confirmation-dialog>
                    </v-row>
                    <v-divider />
                  </v-container>
                  <v-container fluid>
                    <validation-observer v-if="addingClosure">
                      <v-row>
                        <v-col cols="12">
                          <v-row justify="center">
                            <v-list-item class="pl-2">
                              <v-card-text class="pr-0 pl-1">{{ $t('location.addNewClosure') }}</v-card-text>
                              <v-spacer />
                              <span class="d-inline-flex">
                                <v-btn
                                  :small="$vuetify.breakpoint.width < 400"
                                  class="mr-1"
                                  color="primary"
                                  outlined
                                  @click="addClosure(newSpecialClosure)"
                                >
                                  <v-icon>mdi-check</v-icon>
                                </v-btn>
                                <v-btn
                                  :small="$vuetify.breakpoint.width < 400"
                                  color="primary"
                                  outlined
                                  @click="cancelAddNewSpecialClosure"
                                >
                                  <v-icon>mdi-close</v-icon>
                                </v-btn>
                              </span>
                            </v-list-item>
                          </v-row>
                        </v-col>
                        <o-date-time-range-filter
                          v-model="newSpecialClosure"
                          :validation-ref="'addclosure'"
                          :ref="'addclosure'"
                        />
                      </v-row>
                    </validation-observer>
                    <v-row v-else justify="center">
                      <v-btn class="ma-2" color="primary" outlined style="max-width: 97%" @click="addingClosure = true">
                        {{ $t('location.addNewClosure') }}
                      </v-btn>
                    </v-row>
                  </v-container>
                </v-card>
                <v-card class="mt-1" outlined>
                  <v-container class="pt-0 pb-1" fluid>
                    <v-card-title>
                      <div class="overline mb-4">{{ $t('location.specialClosuresDefinedByAdmin') }}</div>
                    </v-card-title>
                    <v-row
                      v-for="(globalClosure, index) of loadedSettings.globalSpecialClosures"
                      v-bind:key="`${globalClosure._id}-${index}`"
                    >
                      <template v-if="showGlobalSpecialClosures(globalClosure)">
                        <v-col class="pb-1 pt-1 mb-2" cols="12">
                          <v-divider />
                        </v-col>
                        <o-date-time-range-filter
                          v-model="loadedSettings.globalSpecialClosures[index]"
                          :validation-ref="'globalClosure'"
                          :ref="'globalClosure'"
                          disabled
                          class="mt-2"
                        />
                      </template>
                    </v-row>
                  </v-container>
                </v-card>
              </v-container>
            </v-tab-item>
            <!-- settings -->
            <v-tab-item>
              <v-container fluid>
                <v-row>
                  <v-col cols="12" lg="4" md="6" v-if="location.type !== 'warehouse'">
                    <v-card outlined>
                      <v-list>
                        <v-list-item>
                          <v-list-item-content>
                            <div class="overline mb-4">
                              {{ $t('location.servicesActivation') }}
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-switch
                            v-model="location.services.delivery_in_store"
                            :label="$t('location.activateInStorePickup')"
                          ></v-switch>
                        </v-list-item>
                        <v-list-item>
                          <v-switch
                            v-model="location.services.click_and_collect"
                            :label="$t('location.activateClickAndCollect')"
                          ></v-switch>
                        </v-list-item>
                        <v-list-item>
                          <v-switch
                            v-model="location.services.click_and_reserve"
                            :label="$t('location.activateEreservation')"
                          ></v-switch>
                        </v-list-item>
                        <v-list-item>
                          <v-switch
                            v-model="location.services.ship_from_store"
                            :label="$t('location.activateShipFromStore')"
                          ></v-switch>
                        </v-list-item>
                      </v-list>
                    </v-card>
                  </v-col>
                  <v-col v-if="isAdmin && location._id != undefined" cols="12" lg="4" md="6">
                    <v-card outlined>
                      <v-list>
                        <v-list-item>
                          <v-list-item-content>
                            <div class="overline mb-4">
                              {{ $t('location.definitiveClosure') }}
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-switch
                            v-model="location.definitive_closure"
                            :label="$t('location.definitiveClosure')"
                          ></v-switch>
                        </v-list-item>
                      </v-list>
                    </v-card>
                  </v-col>
                  <v-col cols="12" lg="4" md="6" v-if="location.type !== 'warehouse'">
                    <v-card outlined>
                      <v-list>
                        <v-list-item>
                          <v-list-item-content>
                            <div class="overline mb-4">
                              {{ $t('appointment.appointment') }}
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <v-switch
                            v-model="location.in_store_appointment.activate_appointments"
                            :label="$t('location.activateAppointments')"
                          ></v-switch>
                        </v-list-item>
                        <v-list-item>
                          <validation-provider
                            v-slot="{ errors }"
                            name="nb_appointments"
                            rules="min_value:1|max_value:50"
                          >
                            <v-text-field
                              v-model="location.in_store_appointment.num_simultaneous_appointments"
                              :error-messages="errors"
                              :label="$t('appointment.simultaneous_appointments')"
                              type="number"
                            ></v-text-field>
                          </validation-provider>
                        </v-list-item>
                      </v-list>
                    </v-card>
                  </v-col>
                  <v-col v-if="isAdmin" cols="12" lg="4" md="6">
                    <v-card outlined>
                      <v-list>
                        <v-list-item>
                          <v-list-item-content>
                            <div class="overline mb-4">
                              {{ $t('location.additionalSettings') }}
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                          <validation-provider
                            v-slot="{ errors }"
                            :name="$t('location.scoring')"
                            rules="required|min_value:0|max_value:10000|integer"
                          >
                            <v-text-field
                              v-model="location.scoring"
                              :error-messages="errors"
                              :label="$t('location.scoring')"
                              required
                              type="number"
                            ></v-text-field>
                          </validation-provider>
                        </v-list-item>
                      </v-list>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-data-table
                      :footer-props="footerProps"
                      :header-props="headerProps"
                      :headers="customServicesHeaders"
                      :items="location.custom_services"
                      :loading="loading"
                      :loading-text="$t('location.customServicesLoadingText')"
                      :no-data-text="$t('location.noCustomService')"
                      :options.sync="options"
                      dense
                      item-key="service_id"
                      mobile-breakpoint="768"
                    >
                      <template v-slot:top>
                        <v-toolbar flat>
                          <p>{{ $t('location.customServices') }}</p>
                        </v-toolbar>
                      </template>
                      <template v-slot:item.activate="{ item }">
                        <v-switch v-model="item.activate" :loading="loading" dense></v-switch>
                      </template>
                      <template v-slot:item.service_id="{ item }">
                        <span style="text-transform: none">{{ item.service_id }}</span>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
            <v-tab-item>
              <v-container fluid>
                <v-data-table
                  :headers="customFieldsHeaders"
                  :items="location.custom_fields"
                  disable-sort
                  hide-default-footer
                  item-key="id"
                  mobile-breakpoint="768"
                >
                  <template v-if="isCustomerService" v-slot:top>
                    <v-toolbar flat>
                      <v-spacer></v-spacer>
                      <v-btn class="mr-1" color="primary" outlined @click="addCustom()">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </v-toolbar>
                  </template>
                  <template v-slot:item.value="{ item }">
                    <v-textarea
                      v-if="typeof item.value == 'string' && item.value.length > 28"
                      :value="item.value"
                      auto-grow
                      hide-details
                      readonly
                    />
                    <span v-else>{{ item.value }}</span>
                  </template>
                  <template v-slot:item.action="{ item }">
                    <v-icon class="mr-2" color="primary" small @click="updateCustom(item)">mdi-pencil</v-icon>
                    <v-icon color="error" small @click="showDeleteDialog(item)">mdi-delete</v-icon>
                  </template>
                </v-data-table>
              </v-container>
            </v-tab-item>
          </v-tabs>
          <v-toolbar flat height="auto" justify-content="center">
            <v-row justify="center">
              <v-btn
                :disabled="loading"
                :loading="loading"
                class="ma-2 mt-3 mb-3"
                color="primary"
                outlined
                type="submit"
              >
                {{ $t('location.save') }}
              </v-btn>
              <v-btn
                v-if="location._id && isOctipasAdmin"
                :disabled="loading"
                :loading="loading"
                class="ma-2 mt-3 mb-3"
                color="primary"
                outlined
                @click="locationDeletion = true"
              >
                {{ $t('location.delete') }}
              </v-btn>
            </v-row>
          </v-toolbar>
        </v-form>
      </validation-observer>
      <confirmation-dialog
        v-if="locationDeletion"
        v-model="locationDeletion"
        :text="$t('location.deletionDialogText')"
        :title="$t('location.deletionDialogTitle')"
        @no="locationDeletion = false"
        @yes="deleteLocation(location.location_id)"
      ></confirmation-dialog>
      <confirmation-dialog
        v-if="showDeleteCustomDialog"
        v-model="showDeleteCustomDialog"
        :text="$t('location.deletionCustomDialogText')"
        :title="$t('location.deletionCustomDialogTitle')"
        @no="showDeleteCustomDialog = false"
        @yes="deleteCustomItem(custom)"
      ></confirmation-dialog>

      <duplicate-time-slot-dialog
        v-model="showDuplicateTimeSlotModal"
        :title="timeSlotToDuplicate.title"
        :timeSlotToDuplicate="timeSlotToDuplicate"
        @no="showDuplicateTimeSlotModal = false"
        @yes="duplicateTimeSlots"
      ></duplicate-time-slot-dialog>

      <custom-form
        v-if="custom"
        :key="formKey"
        v-model="showCustomFormDialog"
        :custom="custom"
        :isNew="isNew"
        @close="close"
        @validate="validate"
      />
    </v-card>
  </v-container>
</template>

<script>
  import { ValidationObserver, ValidationProvider } from 'vee-validate'

  import { EventBus } from '../../event-bus'
  import { mapState } from 'vuex'

  import confirmationDialog from '../../components/ConfirmationDialog'
  import duplicateTimeSlotsDialog from '../../components/dialogs/DuplicateTimeSlotsDialog'
  import CustomForm from '@/components/CustomForm'
  import { LocationService } from '@/services/locationService'
  import { weekDays } from '@/helpers/helper'
  import ODateTimeRangeFilter from '../../components/inputs/ODateTimeRangeFilter.vue'
  import CountriesAutocomplete from '@/components/CountriesAutocomplete'

  const timezones = require('../../assets/timezones.json')

  export default {
    name: 'locationDetails',
    components: {
      'validation-provider': ValidationProvider,
      'validation-observer': ValidationObserver,
      'confirmation-dialog': confirmationDialog,
      'duplicate-time-slot-dialog': duplicateTimeSlotsDialog,
      CustomForm,
      ODateTimeRangeFilter,
      CountriesAutocomplete,
    },
    data() {
      return {
        editing: false,
        countryAreas: [],
        // Live search text of the area comboboxes, mirrored via @update:search-input. Used
        // both to pin the value being typed on top of the suggestions (see buildAreaOptions)
        // and to avoid losing a value typed but not yet committed (no blur/enter) on save.
        area1Search: null,
        area2Search: null,
        // Guards the cascade watchers so loading an existing location does not
        // wipe its zoning; only user-driven parent changes reset the children.
        isMappingLocation: false,
        location: {
          name: undefined,
          location_id: undefined,
          type: undefined,
          establishment_type: undefined,
          timezone: 'etc/UTC',
          contact: {
            email: undefined,
            phone_number: undefined,
          },
          address: {
            address_line_1: undefined,
            address_line_2: undefined,
            city: undefined,
            postal_code: undefined,
            country_id: '',
            latitude: undefined,
            longitude: undefined,
            world_area: undefined,
            country_area_1: undefined,
            country_area_2: undefined,
          },
          opening_hours: weekDays(),
          service_opening_hours: [],
          special_closures: [],
          exceptional_opening_hours: [],
          services: {
            delivery_in_store: true,
            click_and_collect: true,
            click_and_reserve: true,
            ship_from_store: true,
          },
          scoring: 1,
          in_store_appointment: {
            activate_appointments: false,
            num_simultaneous_appointments: 1,
          },
        },
        typeOptions: [
          { text: this.$t('location.warehouse'), value: 'warehouse' },
          { text: this.$t('location.shop'), value: 'shop' },
        ],
        establishmentTypeOptions: [
          { text: this.$t('location.affiliated'), value: 'affiliated' },
          { text: this.$t('location.franchise'), value: 'franchise' },
          { text: this.$t('location.succursale'), value: 'succursale' },
          { text: this.$t('location.corner'), value: 'corner' },
          { text: this.$t('location.outlet'), value: 'outlet' },
          { text: this.$t('location.other'), value: 'other' },
        ],
        hoursServicesLabel: {
          preparation: {
            text: 'preparationModalText',
            title: 'preparationModalTitle',
          },
          appointment: {
            text: 'appointmentModalText',
            title: 'preparationModalTitle',
          },
        },
        newSpecialClosure: { begin: undefined, end: undefined },
        newExceptionalHours: { date: undefined, hours: [] },
        addingClosure: false,
        locationDeletion: false,
        timezones: timezones,
        specialClosureInModificationIndex: undefined,
        specialClosureDeletion: false,
        loading: false,
        addingNewExceptionalHour: false,
        menu: false,
        exceptionalHourInModificationIndex: undefined,
        minDateExceptionalHours: this.$moment().format('YYYY-MM-DD'),
        showCustomFormDialog: false,
        custom: null,
        isNew: true,
        formKey: Date.now(),
        showDeleteCustomDialog: false,
        selectedTab: null,
        options: { itemsPerPage: 15 },
        showHoursModal: false,
        showDuplicateTimeSlotModal: false,
        timeSlotToDuplicate: {},
        selectedTimeSlots: null,
        loadedSettings: { globalSpecialClosures: [], activatedServiceHours: [] },
        firstDayOfWeek: this.$moment.localeData().firstDayOfWeek(),
      }
    },
    computed: {
      ...mapState(['currentStore', 'isCustomerService', 'isAdmin', 'isOctipasAdmin', 'locale']),
      existingExceptionalHoursDates: function () {
        let existingExceptionalHoursDates = []
        if (this.location && this.location.exceptional_opening_hours) {
          for (let existingExceptionalHours of this.location.exceptional_opening_hours) {
            existingExceptionalHoursDates.push(existingExceptionalHours.date)
          }
        }
        return existingExceptionalHoursDates
      },
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
      // Existing first-level zones (from the DB tree) under the selected country.
      // Used as the reference set to decide whether a value is a brand new zone.
      existingArea1Names() {
        const country = this.countryAreas.find((entry) => entry.country_id === this.location.address.country_id)
        return (country?.country_area_1 ?? []).map((area) => area.name)
      },
      // Existing second-level zones under the selected country + first-level zone.
      existingArea2Names() {
        const country = this.countryAreas.find((entry) => entry.country_id === this.location.address.country_id)
        const area1 = country?.country_area_1?.find((area) => area.name === this.location.address.country_area_1)
        return area1?.country_area_2 ?? []
      },
      // First-level zone suggestions (see buildAreaOptions for the pinning logic).
      area1Options() {
        return this.buildAreaOptions(this.area1Search, this.location.address.country_area_1, this.existingArea1Names)
      },
      // Second-level zone suggestions, same derivation as area1Options.
      area2Options() {
        return this.buildAreaOptions(this.area2Search, this.location.address.country_area_2, this.existingArea2Names)
      },
      customFieldsHeaders() {
        return [
          {
            text: this.$t('custom.customFieldName'),
            value: 'name',
          },
          {
            text: this.$t('custom.customFieldValue'),
            value: 'value',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
        ]
      },
      computedLatitude: {
        get() {
          return this.location.address.latitude
        },
        set(newValue) {
          this.location.address.latitude = ![null, undefined, ''].includes(newValue) ? parseFloat(newValue) : newValue
        },
      },
      computedLongitude: {
        get() {
          return this.location.address.longitude
        },
        set(newValue) {
          this.location.address.longitude = ![null, undefined, ''].includes(newValue) ? parseFloat(newValue) : newValue
        },
      },
      footerProps() {
        return {
          'items-per-page-options': [1, 5, 15, 25, 50],
          'items-per-page-text': this.$t('location.customServicesPerPage'),
        }
      },
      customServicesHeaders() {
        return [
          {
            text: this.$t('location.customServiceId'),
            value: 'service_id',
          },
          {
            text: this.$t('location.displayLabel'),
            value: 'display_label',
          },
          {
            text: this.$t('location.active'),
            value: 'activate',
          },
          {
            text: '',
            value: 'actions',
            sortable: false,
          },
        ]
      },
      headerProps() {
        return {
          sortByText: this.$t('location.sortByText'),
        }
      },
      newExceptionalHoursDisplayedDate: {
        get() {
          if (!this.newExceptionalHours?.date) {
            return undefined
          }
          return this.$moment(this.newExceptionalHours.date).format('L')
        },
        set(date) {
          this.newExceptionalHours.date = date
        },
      },
    },
    async mounted() {
      if (this.$store.state.currentTab) {
        this.selectedTab = this.$store.state.currentTab
      }
      // Get the zoning tree that drives the cascading area inputs
      this.getAreas()

      // getSettings must resolve before get() so activatedServiceHours is set when mapLocation runs
      await this.getSettings()
      if (this.$route.params.id && this.$route.params.id !== 'add') {
        this.get()
      }
    },
    methods: {
      onSubmit: function () {
        // A v-combobox does not commit free-typed text to the model until blur/enter,
        // and clicking "save" blurs the input only after this handler runs. Commit any
        // in-progress area text explicitly so it is taken into account.
        this.commitPendingAreas()
        this.$refs.form.validate().then((success) => {
          if (success) {
            if (this.location._id) {
              this.update()
            } else {
              this.create()
            }
          } else {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('location.checkFormFields'),
              color: 'error',
            })
          }
        })
      },
      async getAreas() {
        this.countryAreas = await LocationService.getAreas(this.$auth.accessToken)
      },
      // Mirrors the backend zoning normalization (trim + collapse spaces + lower-case).
      // Not needed for persistence (the API normalizes on write); its purpose is to align
      // a freshly typed value (e.g. "North") with the existing canonical option ("north")
      // so it resolves against the cascade instead of being treated as a new entry.
      normalizeAreaLabel(value) {
        return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').toLowerCase() : value
      },
      // Builds a cascade combobox's item list: the value being entered (live search text,
      // else the committed model) pinned on top with a divider when it is a brand new zone,
      // otherwise just the existing suggestions. Comparison is normalized so a case/spacing
      // variant of an existing zone is not wrongly flagged as new.
      buildAreaOptions(search, model, existingNames) {
        const candidate = search != null ? search : model
        const trimmed = typeof candidate === 'string' ? candidate.trim() : ''
        const isNew = trimmed && !existingNames.includes(this.normalizeAreaLabel(trimmed))
        if (!isNew) {
          return existingNames
        }
        return existingNames.length ? [trimmed, { divider: true }, ...existingNames] : [trimmed]
      },
      // Combobox filter: match against the TRIMMED query so a trailing/leading space typed
      // mid-entry does not filter out the pinned value (whose label is trimmed) until the
      // next character. Non-string items (the divider) never match a text search.
      areaFilter(item, queryText) {
        if (typeof item !== 'string') {
          return false
        }
        return item.toLocaleLowerCase().includes((queryText ?? '').trim().toLocaleLowerCase())
      },
      // Commits area text that was typed but not yet validated by the combobox
      // (no blur/enter), e.g. when the user clicks "save" while still focused.
      commitPendingAreas() {
        const address = this.location.address
        const pending1 = typeof this.area1Search === 'string' ? this.area1Search.trim() : ''
        if (address.country_id && pending1 && this.normalizeAreaLabel(pending1) !== address.country_area_1) {
          address.country_area_1 = pending1
          this.onAreaCommit(1)
        }
        // Only commit a sub-zone when its parent zone is set, otherwise stale
        // search text from a now-cleared parent would re-inject an orphan value
        // (e.g. country_area_1 = null but country_area_2 still filled).
        const pending2 = typeof this.area2Search === 'string' ? this.area2Search.trim() : ''
        if (address.country_area_1 && pending2 && this.normalizeAreaLabel(pending2) !== address.country_area_2) {
          address.country_area_2 = pending2
          this.onAreaCommit(2)
        }
      },
      // On commit of a zone combobox, canonicalize the typed value so it aligns with the
      // existing (lower-cased) suggestions. The value is NOT injected into countryAreas:
      // the combobox already keeps free text as its model value, and mutating the
      // suggestion tree would pile up every intermediate edit as a stale option.
      onAreaCommit(level) {
        const address = this.location.address
        if (!address.country_id) {
          return
        }
        if (level === 1) {
          address.country_area_1 = this.normalizeAreaLabel(address.country_area_1)
        } else {
          address.country_area_2 = this.normalizeAreaLabel(address.country_area_2)
        }
      },
      create: function () {
        this.loading = true
        let location = JSON.parse(JSON.stringify(this.location))
        if (Array.isArray(location.special_closures)) {
          for (let specialClosure of location.special_closures) {
            specialClosure.begin = this.$moment(new Date(specialClosure.begin)).format('YYYY-MM-DDTHH:mm:ss' + '.000Z')
            specialClosure.end = this.$moment(new Date(specialClosure.end)).format('YYYY-MM-DDTHH:mm:ss' + '.000Z')
          }
        }
        let myInit = {
          body: location,
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.post('locations', '/v1/int/locations', myInit)
          .then(() => {
            this.loading = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('location.locationCreated'),
              color: 'success',
            })
            this.$router.push('/locations')
          })
          .catch((e) => {
            console.log(e)
            this.loading = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: e.message,
              color: 'error',
            })
          })
      },
      update: function () {
        this.loading = true
        let location = structuredClone(this.location)
        if (Array.isArray(location.special_closures)) {
          for (let specialClosure of location.special_closures) {
            specialClosure.begin = this.$moment(new Date(specialClosure.begin)).format('YYYY-MM-DDTHH:mm:ss' + '.000Z')
            specialClosure.end = this.$moment(new Date(specialClosure.end)).format('YYYY-MM-DDTHH:mm:ss' + '.000Z')
          }
        }
        let myInit = {
          body: location,
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.put('locations', '/v1/int/locations/' + this.location.location_id, myInit)
          .then((res) => {
            this.loading = false
            if (Array.isArray(res?.location?.special_closures)) {
              for (let specialClosure of res.location.special_closures) {
                specialClosure.begin = this.$moment(new Date(specialClosure.begin)).format('YYYY-MM-DD HH:mm')
                specialClosure.end = this.$moment(new Date(specialClosure.end)).format('YYYY-MM-DD HH:mm')
              }
            }
            if (res?.location?.exceptional_opening_hours) {
              res.location.exceptional_opening_hours = res.location.exceptional_opening_hours.sort(function (a, b) {
                return a.date > b.date ? 1 : a.date < b.date ? -1 : 0
              })
            } else {
              res.location.exceptional_opening_hours = []
            }
            this.mapLocation(res.location)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('location.locationUpdated'),
              color: 'success',
            })
          })
          .catch((e) => {
            this.loading = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: e.message,
              color: 'error',
            })
          })
      },
      deleteLocation(location_id) {
        this.loading = true
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }

        this.$Amplify.API.del('locations', `/v1/int/locations/${location_id}`, myInit)
          .then(() => {
            this.loading = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('location.locationDeleted'),
              color: 'success',
            })
            this.$router.push({ name: 'locations' })
          })
          .catch((e) => {
            this.loading = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: e.message,
              color: 'error',
            })
          })
      },
      get: function () {
        this.loading = true

        if (!this.isCustomerService && this.$route.params.id !== this.currentStore) {
          this.$router.push({ path: `/locations/${this.currentStore}` })
          this.$route.params.id = this.currentStore
        }

        let path = '/v1/int/locations/' + this.$route.params.id
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.get('locations', path, myInit)
          .then((res) => {
            if (Array.isArray(res?.special_closures)) {
              for (let specialClosure of res.special_closures) {
                specialClosure.begin = this.$moment(new Date(specialClosure.begin)).format('YYYY-MM-DD HH:mm')
                specialClosure.end = this.$moment(new Date(specialClosure.end)).format('YYYY-MM-DD HH:mm')
              }
            }
            if (res.exceptional_opening_hours) {
              res.exceptional_opening_hours = res.exceptional_opening_hours.sort(function (a, b) {
                return a.date > b.date ? 1 : a.date < b.date ? -1 : 0
              })
            } else {
              res.exceptional_opening_hours = []
            }
            this.mapLocation(res)
          })
          .catch((e) => {
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: 'error during location loading',
              color: 'error',
            })
          })
        this.loading = false
      },
      addTimeSlot: function (timeSlots) {
        timeSlots.push({ begin: '', end: '' })
      },
      selectTimeSlotsToDuplicate(title, context, day, serviceIndex, isServiceHours = false) {
        // Get either Opening hours or preparation / appointment opening hours
        const days =
          isServiceHours && serviceIndex !== undefined
            ? this.location.service_opening_hours[serviceIndex].opening_hours
            : this.location[context]

        // Get selected time slots
        this.selectedTimeSlots = days?.[day] || []

        // Build time slot objet to be duplicated
        this.timeSlotToDuplicate = {
          context,
          day,
          title: isServiceHours ? this.$t('location.serviceHours.hoursOf') + ' ' + this.$t(title) : this.$t(title),
          serviceIndex: isServiceHours ? serviceIndex : undefined,
          timeSlots: this.selectedTimeSlots.map((slot) => `${slot.begin}-${slot.end}`).join(' / '),
        }
        this.showDuplicateTimeSlotModal = true
      },
      duplicateTimeSlots(selectedDays) {
        const { context, serviceIndex } = this.timeSlotToDuplicate
        // Replace time slot for the selected one, for each selected days ogf the week
        for (const day of selectedDays) {
          const copiedSlots = structuredClone(this.selectedTimeSlots)
          if (serviceIndex !== undefined) {
            // Preparation or appointment opening hours
            this.location.service_opening_hours[serviceIndex].opening_hours[day] = copiedSlots
          } else {
            //  Opening hours
            this.location[context][day] = copiedSlots
          }
        }
        this.showDuplicateTimeSlotModal = false
      },
      removeTimeSlot: function (index, timeSlots) {
        timeSlots.splice(index, 1)
      },
      addClosure: function () {
        this.$refs.addclosure.validate().then((success) => {
          if (success) {
            this.location.special_closures.push(this.newSpecialClosure)
            this.newSpecialClosure = { begin: '', end: '' }
            this.addingClosure = false
          }
        })
      },
      activateSpecialClosureEdition: function (index) {
        if (this.specialClosureInModificationIndex == undefined) {
          this.specialClosureInModificationIndex = index
        } else {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('location.anotherSpecialClosureAlreadyInModification'),
            color: 'error',
          })
        }
      },
      validateSpecialClosureEdition: function (index) {
        this.$refs['specialClosuresModifications'][index].validate().then((success) => {
          if (success) {
            this.specialClosureInModificationIndex = undefined
          }
        })
      },
      removeSpecialClosure: function (index) {
        this.specialClosureDeletion = false
        this.specialClosureInModificationIndex = undefined
        this.removeTimeSlot(index, this.location.special_closures)
      },
      cancelAddNewSpecialClosure: function () {
        this.addingClosure = false
        this.newSpecialClosure = { begin: '', end: '' }
      },
      validateExceptionalHoursDate: function () {
        this.$refs.exceptionalHour.validate().then((success) => {
          if (success) {
            this.location.exceptional_opening_hours.push(this.newExceptionalHours)
            this.newExceptionalHours = { date: undefined, hours: [] }
            this.addingNewExceptionalHour = false
          }
        })
      },
      addNewExceptionalHour: function () {
        this.addingNewExceptionalHour = true
        this.addTimeSlot(this.newExceptionalHours.hours)
      },
      cancelAddNewExceptionalHour: function () {
        this.addingNewExceptionalHour = false
        this.newExceptionalHours.date = undefined
        this.removeTimeSlot(0, this.newExceptionalHours.hours)
      },
      activateExceptionalHourEdition: function (index) {
        if (this.exceptionalHourInModificationIndex == undefined) {
          this.exceptionalHourInModificationIndex = index
        } else {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('location.anotherExceptionalHourAlreadyInModification'),
            color: 'error',
          })
        }
      },
      async validateExceptionalHourEdition(index, timeSlotHours) {
        if (Array.isArray(timeSlotHours) && !timeSlotHours.length) {
          this.exceptionalHourInModificationIndex = undefined
        } else {
          for (let provider of this.$refs['exceptionalHoursModifications' + index]) {
            if (!(await provider.validate())) {
              return false
            }
          }
          this.exceptionalHourInModificationIndex = undefined
        }
      },
      removeExceptionalTimeSlot: function (indexDate) {
        this.exceptionalHourInModificationIndex = undefined
        this.removeTimeSlot(indexDate, this.location.exceptional_opening_hours)
      },
      updateCustom(custom) {
        this.custom = custom
        this.formKey = Date.now()
        this.showCustomFormDialog = true
        this.isNew = false
      },
      close() {
        this.showCustomFormDialog = false
      },
      validate(values) {
        if (this.isNew) {
          this.location.custom_fields.push(values)
        } else {
          let customIndex = this.findIndex(values)
          if (customIndex) {
            this.$set(this.location.custom_fields, customIndex, values)
          }
        }
        this.showCustomFormDialog = false
      },
      addCustom() {
        this.custom = {
          name: undefined,
          value: undefined,
        }
        this.formKey = Date.now()
        this.showCustomFormDialog = true
        this.isNew = true
      },
      deleteCustomItem(item) {
        let customIndex = this.findIndex(item)
        this.location.custom_fields.splice(customIndex, 1)
        this.showDeleteCustomDialog = false
      },
      findIndex(item) {
        return this.location.custom_fields.findIndex((custom) => custom._id === item._id)
      },
      showDeleteDialog(item) {
        this.showDeleteCustomDialog = true
        this.custom = item
      },
      mapLocation(fetchedLocation) {
        // Prevent the cascade watchers from clearing the zoning while we populate
        // the form with the fetched values. The flag is re-enabled in a nextTick
        // registered AFTER the assignment below, so it runs after the watcher
        // flush (Vue runs nextTick callbacks in registration order).
        this.isMappingLocation = true
        this.location = {
          _id: fetchedLocation._id,
          name: fetchedLocation.name,
          location_id: fetchedLocation.location_id,
          type: fetchedLocation.type,
          establishment_type: fetchedLocation.establishment_type,
          timezone: fetchedLocation.timezone,
          contact: {
            email: fetchedLocation.contact?.email,
            phone_number: fetchedLocation.contact?.phone_number,
          },
          address: {
            address_line_1: fetchedLocation.address.address_line_1,
            address_line_2: fetchedLocation.address.address_line_2,
            city: fetchedLocation.address.city,
            postal_code: fetchedLocation.address.postal_code,
            country_id: fetchedLocation.address.country_id,
            latitude: fetchedLocation.address.latitude,
            longitude: fetchedLocation.address.longitude,
            // Normalize on load so legacy upper-cased values align with the canonical
            // (lower-cased) cascade options: sub-zone suggestions resolve and the
            // selected value highlights instead of being treated as free text.
            world_area: this.normalizeAreaLabel(fetchedLocation.address.world_area),
            country_area_1: this.normalizeAreaLabel(fetchedLocation.address.country_area_1),
            country_area_2: this.normalizeAreaLabel(fetchedLocation.address.country_area_2),
          },
          opening_hours: fetchedLocation.opening_hours,
          service_opening_hours: (this.loadedSettings.activatedServiceHours ?? []).map((item) => {
            const foundService = fetchedLocation?.service_opening_hours?.find((i) => i.name === item) || {}
            return {
              name: item,
              activate: foundService?.activate ?? false,
              opening_hours: foundService?.opening_hours ?? structuredClone(weekDays()),
              text: this.hoursServicesLabel[item],
              title: this.hoursServicesLabel[item],
            }
          }),
          special_closures: fetchedLocation.special_closures,
          exceptional_opening_hours: fetchedLocation.exceptional_opening_hours,
          services: fetchedLocation.services,
          scoring: fetchedLocation.scoring,
          in_store_appointment: fetchedLocation.in_store_appointment,
          custom_services: fetchedLocation.custom_services,
          custom_fields: fetchedLocation.custom_fields,
          definitive_closure: fetchedLocation.definitive_closure,
        }
        // Registered after the assignment above so it runs after the cascade
        // watchers have flushed, leaving the loaded zoning intact.
        this.$nextTick(() => {
          this.isMappingLocation = false
        })
      },
      toggleHoursServices(service, confirmed) {
        // Close the modal
        this.$set(service, 'showModal', false)
        if (!confirmed) {
          // If not confirmed, reverse the current activation state
          service.activate = !service.activate
        }
      },
      copyOpeningHours(service) {
        const index = this.location.service_opening_hours.findIndex((item) => item.name === service.name)
        this.$set(
          this.location.service_opening_hours[index],
          'opening_hours',
          structuredClone(this.location.opening_hours)
        )
      },
      validateTimeSlots(timeSlots) {
        return timeSlots.every((slot) => slot.begin && slot.end && slot.begin.trim() !== '' && slot.end.trim() !== '')
      },
      showGlobalSpecialClosures(specialClosure) {
        return (
          specialClosure?.locations?.includes(this.$route.params.id) && Date.now() - new Date(specialClosure.end) < 0
        )
      },
      async getSettings() {
        this.loading = true
        this.$set(this.loadedSettings, 'globalSpecialClosures', [])

        let res = await LocationService.getSettings(this.$auth.accessToken)
        if (res.hours?.displayed_services) {
          this.$set(this.loadedSettings, 'activatedServiceHours', res.hours.displayed_services)
        }
        if (Array.isArray(res.global_special_closures)) {
          this.$set(this.loadedSettings, 'globalSpecialClosures', res.global_special_closures)
        }
        this.loading = false
      },
    },
    watch: {
      selectedTab: function (newVal) {
        this.$store.commit('setCurrentTab', newVal)
      },
      // Changing the country invalidates the selected zones below it.
      'location.address.country_id': function () {
        if (this.isMappingLocation) {
          return
        }
        this.location.address.country_area_1 = undefined
        this.location.address.country_area_2 = undefined
        this.area1Search = null
        this.area2Search = null
      },
      // Changing the first-level zone invalidates the second-level one. The
      // pending search text is cleared too, so it can't be re-committed on save
      // while its parent is empty.
      'location.address.country_area_1': function () {
        if (this.isMappingLocation) {
          return
        }
        this.location.address.country_area_2 = undefined
        this.area2Search = null
      },
      locale: {
        handler() {
          this.firstDayOfWeek = this.$moment.localeData().firstDayOfWeek()
        },
      },
    },
  }
</script>
<style scoped>
  .btn {
    margin-top: -2px;
  }

  .btn-mobile {
    width: 100%;
  }

  .row {
    margin: 0 !important;
  }

  .checkbox-container {
    width: 125px;
  }

  /* Zoning labels are stored lower-cased (backend canonicalization); display them
     upper-cased in the form for readability. Cosmetic only — the bound value is
     untouched. */
  .zone-uppercase ::v-deep input {
    text-transform: uppercase;
  }
</style>

<!-- Combobox menus are teleported outside the component, so their items cannot be
     reached by scoped styles; target the menu content class with a global rule. -->
<style>
  .zone-uppercase-menu .v-list-item__title {
    text-transform: uppercase;
  }
</style>
